import {repoAttestationsIndexPath} from '@github-ui/paths'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav/states'
import {useSearchParams} from '@github-ui/use-navigate'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useRef, useState} from 'react'

import type {AttestationsPayload} from '../types'

export function useAttestationsResults(initialPayload: AttestationsPayload, csrfToken: string) {
  const [currentPayload, setCurrentPayload] = useState<AttestationsPayload>(initialPayload)
  const {repo} = initialPayload
  const [isFetching, setIsFetching] = useState(false)
  const latestUrlRef = useRef('')
  const [params, setParams] = useSearchParams()

  function beginFetch() {
    setIsFetching(true)
    document.dispatchEvent(new Event(SOFT_NAV_STATE.PROGRESS_BAR.START))
  }

  function completeFetch() {
    setIsFetching(false)
    document.dispatchEvent(new Event(SOFT_NAV_STATE.PROGRESS_BAR.END))
  }

  async function fetchResults(q?: string, pagination?: {before?: string; after?: string}) {
    try {
      const url = repoAttestationsIndexPath({
        repo,
        pagination,
        query: q || '',
      })

      latestUrlRef.current = url

      beginFetch()
      if (latestUrlRef.current !== url) return // Abort if the URL has changed since the fetch was initiated
      const response = await reactFetchJSON(url)

      // This check ensures that only the latest request is processed, preventing race conditions
      // where outdated responses could overwrite the state if multiple fetches are triggered in quick succession.
      if (latestUrlRef.current !== url) return
      if (!response.ok) return

      const results = await response.json()
      const {payload} = results
      setCurrentPayload(payload)
      completeFetch()

      const browserParams = new URLSearchParams(params.toString())

      if (q) {
        browserParams.set('q', q)
        browserParams.delete('before')
        browserParams.delete('after')
      } else {
        browserParams.delete('q')
      }

      if (pagination?.after) {
        browserParams.delete('before')
        browserParams.set('after', pagination.after)
      } else if (pagination?.before) {
        browserParams.delete('after')
        browserParams.set('before', pagination.before)
      }

      setParams(browserParams)
    } catch {
      completeFetch()
    }
  }

  async function deleteAttestations(selectedIds: string[]) {
    try {
      const requestBody = {ids: selectedIds}
      const deleteUrl = repoAttestationsIndexPath({repo})

      const response = await reactFetchJSON(deleteUrl, {
        method: 'DELETE',
        body: requestBody,
        headers: {
          'Content-Type': 'application/json',
          'Scoped-CSRF-Token': csrfToken,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to delete attestations')
      }

      // refresh the attestations list after successful deletion
      await fetchResults()
    } catch {
      // handle error gracefully and refresh to reset state
      await fetchResults()
    }
  }

  function changePage(
    pageDirection: 'next' | 'previous',
    currentCursors: {startCursor: string; endCursor: string},
    query: string,
  ) {
    const pagination: {before?: string; after?: string} = {}

    if (pageDirection === 'next') {
      pagination.after = currentCursors.endCursor
    } else {
      pagination.before = currentCursors.startCursor
    }

    fetchResults(query, pagination)
  }

  return {
    currentPayload,
    isFetching,
    currentQuery: params.get('q') || '',
    fetchResults,
    deleteAttestations,
    changePage,
  }
}
